import * as React from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Navbar, Footer } from "@/components/landing";
import { ArrowLeft } from "lucide-react";
import { useLocalizedHref } from "@/hooks/useLocalizedHref";
import { useLocale } from "@/hooks/useLocale";

const companyName = "Holy Moly";
const contactEmail = "privacy@holymoly.com";
const lastUpdatedEn = "February 6, 2026";
const lastUpdatedFr = "6 février 2026";

interface TermItem {
  term: string;
  desc: string;
}

export function PrivacyPolicyPage() {
  const { t } = useTranslation();
  const localizedHref = useLocalizedHref();
  const locale = useLocale();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = locale === "fr" ? lastUpdatedFr : lastUpdatedEn;
  const personalItems = t("legal.privacy.sections.information.personalItems", {
    returnObjects: true,
  }) as string[];
  const autoItems = t("legal.privacy.sections.information.autoItems", {
    returnObjects: true,
  }) as string[];
  const useItems = t("legal.privacy.sections.use.items", {
    returnObjects: true,
  }) as string[];
  const legalBasisItems = t("legal.privacy.sections.legalBasis.items", {
    returnObjects: true,
  }) as TermItem[];
  const rightsItems = t("legal.privacy.sections.rights.items", {
    returnObjects: true,
  }) as TermItem[];
  const sharingItems = t("legal.privacy.sections.sharing.items", {
    returnObjects: true,
  }) as TermItem[];

  return (
    <div className="relative min-h-screen">
      <Navbar />

      <main className="pt-28 pb-20">
        <div className="container mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <Link
            to={localizedHref("/")}
            className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-primary/80"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("legal.backToHome")}
          </Link>

          <h1 className="mb-2 text-4xl font-bold tracking-tight text-foreground">
            {t("legal.privacy.title")}
          </h1>
          <p className="mb-12 text-sm text-muted-foreground">
            {t("legal.lastUpdated", { date: lastUpdated })}
          </p>

          {locale !== "en" && (
            <p className="mb-8 text-xs text-muted-foreground italic">{t("legal.disclaimer")}</p>
          )}

          <div className="prose prose-gray max-w-none space-y-8">
            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.privacy.sections.introduction.heading")}
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.privacy.sections.introduction.body", {
                  company: companyName,
                })}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.privacy.sections.information.heading")}
              </h2>

              <h3 className="mt-4 mb-2 text-lg font-semibold text-foreground">
                {t("legal.privacy.sections.information.personalHeading")}
              </h3>
              <p className="mb-3 leading-relaxed text-muted-foreground">
                {t("legal.privacy.sections.information.personalIntro")}
              </p>
              <ul className="list-disc space-y-1 pl-6 text-muted-foreground">
                {personalItems.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
              <p className="mt-3 leading-relaxed text-muted-foreground">
                {t("legal.privacy.sections.information.personalOutro")}
              </p>

              <h3 className="mt-4 mb-2 text-lg font-semibold text-foreground">
                {t("legal.privacy.sections.information.autoHeading")}
              </h3>
              <p className="mb-3 leading-relaxed text-muted-foreground">
                {t("legal.privacy.sections.information.autoIntro")}
              </p>
              <ul className="list-disc space-y-1 pl-6 text-muted-foreground">
                {autoItems.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>

              <h3 className="mt-4 mb-2 text-lg font-semibold text-foreground">
                {t("legal.privacy.sections.information.cookiesHeading")}
              </h3>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.privacy.sections.information.cookiesBody")}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.privacy.sections.use.heading")}
              </h2>
              <p className="mb-3 leading-relaxed text-muted-foreground">
                {t("legal.privacy.sections.use.intro")}
              </p>
              <ul className="list-disc space-y-1 pl-6 text-muted-foreground">
                {useItems.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.privacy.sections.legalBasis.heading")}
              </h2>
              <p className="mb-3 leading-relaxed text-muted-foreground">
                {t("legal.privacy.sections.legalBasis.intro")}
              </p>
              <ul className="list-disc space-y-1 pl-6 text-muted-foreground">
                {legalBasisItems.map((item, idx) => (
                  <li key={idx}>
                    <strong>{item.term}</strong>
                    {item.desc}
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.privacy.sections.rights.heading")}
              </h2>
              <p className="mb-3 leading-relaxed text-muted-foreground">
                {t("legal.privacy.sections.rights.intro")}
              </p>
              <ul className="list-disc space-y-1 pl-6 text-muted-foreground">
                {rightsItems.map((item, idx) => (
                  <li key={idx}>
                    <strong>{item.term}</strong>
                    {item.desc}
                  </li>
                ))}
              </ul>
              <p className="mt-3 leading-relaxed text-muted-foreground">
                {t("legal.privacy.sections.rights.outroLead")}{" "}
                <a
                  href={`mailto:${contactEmail}`}
                  className="font-medium text-primary hover:text-primary/80"
                >
                  {contactEmail}
                </a>
                {t("legal.privacy.sections.rights.outroTail")}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.privacy.sections.security.heading")}
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.privacy.sections.security.body")}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.privacy.sections.retention.heading")}
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.privacy.sections.retention.body")}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.privacy.sections.sharing.heading")}
              </h2>
              <p className="mb-3 leading-relaxed text-muted-foreground">
                {t("legal.privacy.sections.sharing.intro")}
              </p>
              <ul className="list-disc space-y-1 pl-6 text-muted-foreground">
                {sharingItems.map((item, idx) => (
                  <li key={idx}>
                    <strong>{item.term}</strong>
                    {item.desc}
                  </li>
                ))}
              </ul>
              <p className="mt-3 leading-relaxed text-muted-foreground">
                {t("legal.privacy.sections.sharing.outro")}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.privacy.sections.transfers.heading")}
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.privacy.sections.transfers.body")}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.privacy.sections.children.heading")}
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.privacy.sections.children.body")}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.privacy.sections.changes.heading")}
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.privacy.sections.changes.body")}
              </p>
            </section>

            <section>
              <h2 className="mb-3 text-xl font-bold text-foreground">
                {t("legal.privacy.sections.contact.heading")}
              </h2>
              <p className="leading-relaxed text-muted-foreground">
                {t("legal.privacy.sections.contact.body")}
              </p>
              <div className="mt-3 rounded-lg bg-muted p-4">
                <p className="font-semibold text-foreground">{companyName}</p>
                <p className="text-muted-foreground">
                  {t("legal.contactBlock.emailLabel")}{" "}
                  <a
                    href={`mailto:${contactEmail}`}
                    className="font-medium text-primary hover:text-primary/80"
                  >
                    {contactEmail}
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
